import assert from "assert"
import { useMemo } from "react"
import { Card, Grid, Skeleton } from "@mui/material"
import usePaySchedulesDates from "pages/Employee/query-hooks/usePayScheduleDates"
import { useParams } from "react-router-dom"
import { CollapsibleCard, Page, SimpleGrid } from "components"
import { pickErrorMessage } from "utils/pickErrorMessage"
import type { PaydaysPayScheduleDate } from "api"
import { getBuildRegion, BUILD_REGION } from "shell/config"
import Header from "./components/Header/Header"
import { GenerateDates } from "./components/GenerateDates/GenerateDates"
import { GenerateDemoDates } from "./components/GenerateDemoDates/GenerateDemoDates"
import { AddDate } from "./components/AddDate/AddDate"
import { PayScheduleDetails } from "./components/PayScheduleDetails/PayScheduleDetails"
import usePaySchedule from "./query-hooks/usePaySchedule"
import { COLUMNS } from "./columns"
import { PayScheduleProperties } from "./components/PayScheduleProperties/PayScheduleProperties"
import { BulkUploadPaySchedules } from "./components/BulkUploadPaySchedules/BulkUploadPaySchedules"
import { GenerateDatesUS } from "./components/GenerateDatesUS/GenerateDatesUS"

export interface PayScheduleDateWithPayScheduleId
  extends PaydaysPayScheduleDate {
  pay_schedule_id: string
}

const PaySchedulesDatesContent = () => {
  const { id } = useParams()
  assert(id)

  const {
    data: payScheduleDatesData,
    isLoading,
    isFetching,
    error,
  } = usePaySchedulesDates(id)

  const rows = useMemo(() => {
    return (
      payScheduleDatesData?.map((paySchedules) => ({
        ...paySchedules,
        pay_schedule_id: id,
      })) || []
    )
  }, [payScheduleDatesData, id])

  return (
    <>
      <Grid item xs={6}>
        <PayScheduleDetails />
      </Grid>
      <Grid item xs={6}>
        <PayScheduleProperties />
      </Grid>
      <Grid item xs={6}>
        <BulkUploadPaySchedules />
      </Grid>
      <Grid item xs={12}>
        <CollapsibleCard title="Pay Schedules Dates" type="section">
          <Grid container pb={4}>
            <Grid item xs={12} md={6}>
              {getBuildRegion() === BUILD_REGION.US && <GenerateDatesUS />}
              {getBuildRegion() === BUILD_REGION.UK && (
                <>
                  <GenerateDates />
                  <GenerateDemoDates />
                </>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <AddDate />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Card>
              {isLoading ? (
                <Skeleton
                  role="progressbar"
                  height={600}
                  sx={{ transform: "none" }}
                />
              ) : (
                <SimpleGrid
                  idField="pay_schedule_date_id"
                  isLoading={isFetching}
                  rows={rows}
                  columns={COLUMNS}
                  pagination
                  quickFilter
                  initialPaginationPageSize={20}
                  rowsPerPageOptions={[5, 10, 20]}
                  error={pickErrorMessage(error)}
                />
              )}
            </Card>
          </Grid>
        </CollapsibleCard>
      </Grid>
    </>
  )
}

const PaySchedulesDates = () => {
  const { id } = useParams()

  const { data: paySchedule } = usePaySchedule(id)

  return (
    <Page title={paySchedule?.name} header={<Header />}>
      <PaySchedulesDatesContent />
    </Page>
  )
}

export default PaySchedulesDates
